import { useTranslation } from 'react-i18next';

import { AppBar, BubbleStepsProgressBar, SvgIcon } from '../../../Molecules';
import { SVGIconType } from '../../../../constants/svgIcon';
import {
  getQuickCreateGameModeProps,
  QuickCreateGameMode,
} from '../../../../models/DraftStore';
import { isNotEmptyString } from '../../../../utils/string';

import styles from './CreateMemoryGameContentListTemplate.module.css';

type GameContentsProps = {
  question: string;
  answer: string;
  questionPlaceHolder: string;
  answerPlaceHolder: string;
  questionHasTts: boolean;
  answerHasTts: boolean;
};

type CreateMemoryGameContentListTemplateProps = {
  type: QuickCreateGameMode | undefined;
  gameContents: GameContentsProps[];
  isNextDisabled: boolean;
  isExceedLimit: boolean;
  showRequiredWarning: boolean;
  onClickEdit: (index: number, type: string) => void;
  onClickRemove: (index: number) => void;
  onClickAddContent: () => void;
  onClickAudioBar: () => void;
  onClickNext: () => void;
  onClickBack: () => void;
};

export const CreateMemoryGameContentListTemplate = (
  props: CreateMemoryGameContentListTemplateProps,
) => {
  const {
    type,
    gameContents,
    isNextDisabled,
    isExceedLimit,
    showRequiredWarning,
    onClickEdit,
    onClickRemove,
    onClickAddContent,
    onClickAudioBar,
    onClickNext,
    onClickBack,
  } = props;
  const { t } = useTranslation();

  return (
    <div className={styles.createMemoryContentList}>
      <AppBar
        title={type ? t(getQuickCreateGameModeProps(type).name) : ''}
        onClickBack={onClickBack}
      />
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          {/* <p className={styles.gameTypeName}>
            {t(getQuickCreateGameModeProps(type).name)}
          </p> */}
          <p className={styles.gameTypeName}>{t('AI Memory Games')}</p>
        </div>
        <div className={styles.progress}>
          <BubbleStepsProgressBar currentStep={1} totalSteps={3} />
        </div>

        <p className={styles.title}>{t('+New AI Games')}</p>

        {gameContents.map((gameContent, index) => (
          <div key={index} className={styles.gameCard}>
            <div className={styles.gameFooter}>
              <div className={styles.gameNumber}>{index + 1}</div>
              <button
                className={styles.removeButton}
                onClick={() => {
                  onClickRemove(index);
                }}
              >
                <SvgIcon
                  name={SVGIconType.CircleCross}
                  width={24}
                  height={24}
                />
              </button>
            </div>
            <div className={styles.gameHeader}>
              <div className={styles.cardHeader}>
                <SvgIcon
                  name={SVGIconType.LanguageQ}
                  width={24}
                  height={24}
                  stroke={'black'}
                />
                {t('Question')}
              </div>
              <div
                className={styles.cardQuestion}
                style={{
                  color: gameContent.question ? 'black' : '#808080',
                }}
                onClick={() => {
                  onClickEdit(index, 'Question');
                }}
              >
                <div className={styles.cardContentText}>
                  {gameContent.questionHasTts ? (
                    <div
                      className={styles.audioBar}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickAudioBar();
                      }}
                    >
                      <SvgIcon
                        name={SVGIconType.PlayerPlay}
                        width={24}
                        height={24}
                        fill={'#cccccc'}
                      />
                      <div className={styles.audioTrack} />
                    </div>
                  ) : null}
                  {gameContent.question
                    ? gameContent.question
                    : gameContent.questionPlaceHolder}
                  {showRequiredWarning &&
                    !isNotEmptyString(gameContent.question) && (
                      <div className={styles.warningText}>
                        {t('This field is required.')}
                      </div>
                    )}
                </div>
                <SvgIcon
                  className={styles.editButton}
                  name={SVGIconType.Edit}
                  width={24}
                  height={24}
                  stroke={'#808080'}
                />
              </div>
              <div className={styles.cardHeader}>
                <SvgIcon
                  name={SVGIconType.LanguageA}
                  width={24}
                  height={24}
                  stroke={'black'}
                />
                {t('Answer')}
              </div>
              <div
                className={styles.cardContent}
                style={{
                  color: gameContent.answer ? 'black' : '#808080',
                  border:
                    showRequiredWarning && !isNotEmptyString(gameContent.answer)
                      ? '1px solid #D95141'
                      : undefined,
                }}
                onClick={() => {
                  onClickEdit(index, 'Answer');
                }}
              >
                <div className={styles.cardContentText}>
                  {gameContent.answerHasTts ? (
                    <div
                      className={styles.audioBar}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickAudioBar();
                      }}
                    >
                      <SvgIcon
                        name={SVGIconType.PlayerPlay}
                        width={24}
                        height={24}
                        fill={'#cccccc'}
                      />
                      <div className={styles.audioTrack} />
                    </div>
                  ) : null}
                  {gameContent.answer
                    ? gameContent.answer
                    : gameContent.answerPlaceHolder}
                  {showRequiredWarning &&
                    !isNotEmptyString(gameContent.answer) && (
                      <div className={styles.warningText}>
                        {t('This field is required.')}
                      </div>
                    )}
                </div>
                <SvgIcon
                  className={styles.editButton}
                  name={SVGIconType.Edit}
                  width={24}
                  height={24}
                  stroke={'#808080'}
                />
              </div>
            </div>
          </div>
        ))}

        <button className={styles.addButton} onClick={onClickAddContent}>
          {t('+Game')}
        </button>
        <p
          className={styles.gameCount}
          style={{ color: isExceedLimit ? '#D95141' : '#808080' }}
        >
          {t('{count} games (max. {maxCount})', {
            count: gameContents.length,
            maxCount: 20,
          })}
        </p>
      </div>
      <div className={styles.bottomBar}>
        <div className={styles.nextContainer}>
          <button className={styles.nextButton} onClick={onClickBack}>
            <SvgIcon
              className={styles.leftArrowIcon}
              name={SVGIconType.Arrow}
            />
            <span className={styles.nextText}>{t('Back')}</span>
          </button>
          <button
            className={styles.nextButton}
            onClick={onClickNext}
            disabled={isNextDisabled}
          >
            <span className={styles.nextText}>{t('Next')}</span>
            <SvgIcon
              className={styles.rightArrowIcon}
              name={SVGIconType.Arrow}
            />
            {isNextDisabled ? <div className={styles.blackShadow} /> : null}
          </button>
        </div>
      </div>
    </div>
  );
};
